import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { StatusPill } from "../components/UI";

const KIND_MAP = {
  "hire.started": { label: "HIRE STARTED", tone: "green" },
  "hire.ended": { label: "OFF-HIRED", tone: "neutral" },
  "xero.would-post": { label: "WOULD POST TO XERO", tone: "amber" },
  "xero.posted": { label: "POSTED TO XERO — LIVE", tone: "green" },
  "email.would-send": { label: "WOULD EMAIL", tone: "amber" },
  "email.sent": { label: "EMAIL SENT — LIVE", tone: "green" },
  "customer.added": { label: "CUSTOMER ADDED", tone: "blue" },
};

function formatTs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

function EmailRow({ e }) {
  const isLive = e.kind === "email.sent" || e.kind === "xero.posted";
  return (
    <div style={{padding:"10px 0", borderBottom: "1px solid var(--ach-row-divider)"}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap: 12}}>
        <div style={{display:"flex", alignItems:"center", gap:10, flex:1}}>
          <StatusPill tone={isLive ? "green" : "amber"}>{isLive ? "SENT LIVE" : "WOULD SEND"}</StatusPill>
          <span style={{fontSize:13, fontWeight: 600}}>{e.detail}</span>
          {e.created_by_name && (
            <span style={{fontSize:11, color:"var(--ach-text-2)", fontStyle:"italic"}}>· by {e.created_by_name}</span>
          )}
        </div>
        <div style={{fontSize:11, color:"var(--ach-text-2)"}}>{formatTs(e.created_at)}</div>
      </div>
    </div>
  );
}

export default function Emails() {
  const [entries, setEntries] = useState([]);
  useEffect(() => { api.get("/activity-log?limit=200").then(r => setEntries(r.data)); }, []);

  const emailEntries = entries.filter(e => e.kind === "email.would-send" || e.kind === "email.sent");
  const xeroEntries = entries.filter(e => e.kind === "xero.would-post" || e.kind === "xero.posted");
  const other = entries.filter(e => !e.kind.startsWith("email.") && !e.kind.startsWith("xero."));

  const liveEmails = emailEntries.filter(e => e.kind === "email.sent").length;
  const liveXero = xeroEntries.filter(e => e.kind === "xero.posted").length;

  return (
    <div data-testid="emails-page">
      <PageHeader
        title="Emails"
        subtitle="Everything the system has sent — and everything it would have sent if the switch was off"
      />

      <div className="ach-card" style={{marginBottom: 16, background: (liveEmails + liveXero) > 0 ? "var(--ach-green-tint)" : "var(--ach-blue-tint)", borderColor: (liveEmails + liveXero) > 0 ? "var(--ach-green)" : "var(--ach-blue)"}}>
        <div style={{fontSize:13, color: (liveEmails + liveXero) > 0 ? "var(--ach-green)" : "var(--ach-blue)"}}>
          {(liveEmails + liveXero) > 0 ? (
            <><strong>Live mode is on.</strong> {liveEmails} email{liveEmails === 1 ? "" : "s"} and {liveXero} Xero post{liveXero === 1 ? "" : "s"} have actually gone through. Anything switched off still gets logged here.</>
          ) : (
            <><strong>Phase 1 is safe.</strong> Nothing is actually sent to customers or to Xero yet. This screen records what <em>would</em> have gone out so nothing gets missed. Go to Connections and switch things on when you&apos;re ready.</>
          )}
        </div>
      </div>

      <div className="ach-card" style={{marginBottom: 16}} data-testid="card-emails">
        <h2 className="ach-card__title">Emails · {emailEntries.length}</h2>
        {emailEntries.length === 0 ? (
          <div style={{fontSize: 12, color:"var(--ach-text-2)"}}>Nothing yet — approve a month-end to trigger the Hyjac summary email.</div>
        ) : emailEntries.map(e => <EmailRow key={e.id} e={e} />)}
      </div>

      <div className="ach-card" style={{marginBottom: 16}} data-testid="card-xero">
        <h2 className="ach-card__title">Xero · {xeroEntries.length}</h2>
        {xeroEntries.length === 0 ? (
          <div style={{fontSize: 12, color:"var(--ach-text-2)"}}>Nothing yet — approve a month-end to see the posting record.</div>
        ) : xeroEntries.map(e => <EmailRow key={e.id} e={e} />)}
      </div>

      <div className="ach-card">
        <h2 className="ach-card__title">What else the system has been doing</h2>
        <table className="ach-table">
          <thead>
            <tr>
              <th>What happened</th>
              <th>Detail</th>
              <th>By</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {other.length === 0 ? (
              <tr><td colSpan={4} style={{textAlign:"center", padding: 24, color:"var(--ach-text-2)"}}>Nothing recorded yet.</td></tr>
            ) : other.map(e => {
              const km = KIND_MAP[e.kind] || { label: e.kind.toUpperCase(), tone: "neutral" };
              return (
                <tr key={e.id}>
                  <td><StatusPill tone={km.tone}>{km.label}</StatusPill></td>
                  <td>{e.detail}</td>
                  <td style={{fontSize:12, color: e.created_by_name ? "var(--ach-text-1)" : "var(--ach-text-2)"}}>
                    {e.created_by_name || "—"}
                  </td>
                  <td>{formatTs(e.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
